import React, { ForwardRefRenderFunction, forwardRef } from "react";
//#region types
import { IncomingInvoiceTodoSelectionFormContainerProps } from "models/propTypes/incomingInvoicePropTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { IncomingInvoiceFormTypes } from "models/formTypes";
//#endregion
//#region components
import IncomingInvoiceTodoSelectionForm from "./todoSelectionForm";
import useIncomingInvoiceTodoFormHelpers from "./useTodoFormHelpers";
import useBookingTreeItems from "../bookingTree/useBookingTreeItems";
import useIncomingInvoicePermissions from "../usePermissions";
import {
  CostForwardingMarkingProvider,
  getCostForwardingFormValues,
  getCostForwardingReadId,
  useCostForwardingMarking,
  isCostForwardingOffered,
  isForwardableIncomingInvoice,
} from "components/costForwardingMarking";
import useInvoicePermissions from "components/invoice/useInvoicePemissions";
//#endregion

const IncomingInvoiceTodoSelectionFormContainer: ForwardRefRenderFunction<
  FormHandles,
  IncomingInvoiceTodoSelectionFormContainerProps
> = (props, ref) => {
  const { invoice } = props;

  const { getHasIncomingInvoiceSigneePermission } = useIncomingInvoicePermissions();
  const hasSigneePermission = getHasIncomingInvoiceSigneePermission();

  const { validationSchema, initialValues, accountSuggestions, dimensionAmount, getAccountAdditionalInfoType } =
    useIncomingInvoiceTodoFormHelpers(invoice, hasSigneePermission);

  const { searchBookingTreeItemsAutoComplete: getBookingTreeItems } = useBookingTreeItems();

  // Without invoicing rights the marking is not even requested, so the step never flashes up.
  const { canUseInvoices } = useInvoicePermissions();
  const loadedCostForwarding = useCostForwardingMarking(
    "incomingInvoice",
    getCostForwardingReadId(invoice.id, canUseInvoices),
    invoice
  );
  const isForwardable = isForwardableIncomingInvoice(invoice);
  const costForwarding = React.useMemo(
    () => ({
      ...loadedCostForwarding,
      isAvailable: isCostForwardingOffered(!!canUseInvoices, loadedCostForwarding.isAvailable, isForwardable),
    }),
    [loadedCostForwarding, canUseInvoices, isForwardable]
  );
  const supplierName = invoice.supplier?.name ?? invoice.supplierName;

  // Read at the moment the document itself reinitialises the form. A marking that arrives or changes
  // later is patched in by useCostForwardingFormSync, so it never resets the user's other edits.
  const costForwardingValues = getCostForwardingFormValues(
    costForwarding.marking,
    costForwarding.isAvailable,
    supplierName
  );
  const latestCostForwardingValues = React.useRef(costForwardingValues);
  latestCostForwardingValues.current = costForwardingValues;

  const defaultValues = React.useMemo(
    (): IncomingInvoiceFormTypes.Todo => ({ ...initialValues, ...latestCostForwardingValues.current }),
    [initialValues]
  );

  const costForwardingContext = React.useMemo(
    () => ({ ...costForwarding, source: "incomingInvoice" as const, sourceId: invoice.id, supplierName }),
    [costForwarding, invoice.id, supplierName]
  );

  return (
    <CostForwardingMarkingProvider value={costForwardingContext}>
      <IncomingInvoiceTodoSelectionForm
        ref={ref}
        {...props}
        defaultValues={defaultValues}
        validationSchema={validationSchema}
        onFormValidationChange={props.onFormValidationChange}
        accountSuggestions={accountSuggestions}
        getBookingTreeItems={getBookingTreeItems}
        dimensionAmount={dimensionAmount}
        showAccountSection={initialValues.showAccount}
        allwoPeriodisation={initialValues.allowPeriodisation}
        getAccountAdditionalInfoType={getAccountAdditionalInfoType}
      />
    </CostForwardingMarkingProvider>
  );
};

export default forwardRef(IncomingInvoiceTodoSelectionFormContainer);
